
  import { Component, Prop } from 'vue-property-decorator'
  import { FilesProcess } from '@/components/forms/view/FilesProcess'
  import { Stock } from '@/entities/sales'
  import axios from 'axios'
  import { sendError } from '@/utils/notificationError'

  const baseURl = process.env.VUE_APP_BASE_URL

  @Component
  export default class DownloadPhotos extends FilesProcess {
    @Prop({ type: Stock, required: true }) stock!: Stock

    loading = false
    hiddenWithOutPhotos = false
    showError = false
    errorMessage = ''

    async mounted () {
      const photos = await this.getPhotos(this.stock.id)
      this.hiddenWithOutPhotos = !photos.length
    }

    // Función para extraer el nombre del archivo desde Content-Disposition
    getFilenameFromResponse (response: any): string {
      const disposition = response.headers['content-disposition']
      const match = disposition?.match(/filename="(.+)"/)
      return match?.[1] || `${this.stock.auto.name}_fotos.zip`
    }

    // Función para descargar el archivo ZIP
    downloadFile (blob: Blob, filename: string): void {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()

      // Limpiar recursos
      window.URL.revokeObjectURL(url)
      document.body.removeChild(link)
    }

    // Preparar datos para el servicio de compresión
    prepareCompressionData (photos: any[]) {
      return {
        zipName: this.stock.auto.name || 'fotos_vehiculo',
        files: photos.map(photo => {
          const link = photo?.file?.metadata?.l?.uri || photo?.file?.metadata?.uri
          const fileName = `${photo.parameter.description}_${photo.file.id}.jpg`

          return {
            link,
            fileName,
          }
        }).filter(item => item.link), // Filtrar elementos sin link
      }
    }

    // Validar datos antes de enviar al servicio
    validateCompressionData (compressionData: any): boolean {
      if (!compressionData.files.length) {
        this.errorMessage = 'No se encontraron URLs válidas para las fotos'
        this.showError = true
        return false
      }
      return true
    }

    // Llamar al servicio de compresión
    async callCompressionService (compressionData: any) {
      return axios.post(
        `${baseURl}/convert-pdf-to-image/compression/compress`,
        compressionData,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 120000,
        }
      )
    }

    // Procesar respuesta del servicio de compresión
    processCompressionResponse (response: any): void {
      const contentType = response.headers['content-type']

      if (contentType?.includes('application/zip')) {
        const filename = this.getFilenameFromResponse(response)
        this.downloadFile(response.data, filename)
      } else {
        throw new Error('Respuesta inesperada del servidor')
      }
    }

    // Manejar errores de la descarga
    async handleDownloadError (error: any): Promise<void> {
      console.error('Error al descargar fotos:', error)

      // Enviar error para monitoreo
      const context = {
        app_name: 'GENIO',
        view: this.$router.currentRoute.path,
        record_id: `stock-${this.stock.id}`,
        context: `Error descargando fotos del vehículo ${this.stock.auto.name} | Stock ID: ${this.stock.id} | Error: ${JSON.stringify(error)} | Mensaje: Error al comprimir y descargar fotos del vehículo`,
      }

      await sendError(context)

      if (error.response) {
        await this.handleServerError(error.response)
      } else if (error.request) {
        this.errorMessage = 'Error de conexión. Verifica tu conexión a internet.'
      } else {
        this.errorMessage = `Error inesperado: ${error.message}`
      }

      this.showError = true
    }

    // Manejar errores del servidor
    async handleServerError (response: any): Promise<void> {
      if (response.data instanceof Blob) {
        try {
          const errorText = await response.data.text()
          const errorData = JSON.parse(errorText)
          this.errorMessage = `Error del servidor: ${errorData.error || 'Error desconocido'}`
          if (errorData.details) {
            console.error('Detalles del error:', errorData.details)
          }
        } catch {
          this.errorMessage = 'Error del servidor al procesar la solicitud'
        }
      } else {
        this.errorMessage = `Error del servidor: ${response.data?.error || 'Error desconocido'}`
      }
    }

    // Método principal refactorizado
    async downloadPhotos (): Promise<void> {
      try {
        this.loading = true
        this.showError = false

        const photos = await this.getPhotos(this.stock.id)

        if (!photos.length) {
          this.errorMessage = 'No hay fotos disponibles para descargar'
          this.showError = true
          return
        }

        const compressionData = this.prepareCompressionData(photos)

        if (!this.validateCompressionData(compressionData)) {
          return
        }

        const response = await this.callCompressionService(compressionData)
        this.processCompressionResponse(response)
      } catch (error: any) {
        await this.handleDownloadError(error)
      } finally {
        this.loading = false
      }
    }

    // Obtener array de fotos
    async getPhotos (id: number) {
      if (!id) return []

      const filter = {
        _and: [
          { process: { table_name: { _eq: 'stock' } } },
          { file_type: { name: { _eq: 'photo' } } },
        ],
      }

      const photos = await this.fetchData({
        query: { name: 'find', model: 'FileParameter' },
        filter,
        force: true,
      })

      // Ordenar por "visibility.publications.order"
      const fileParametersPhoto = photos.sort(
        (a, b) => a.visibility?.publications?.order - b.visibility?.publications?.order
      )

      const newPhotos = []
      for (const parameter of fileParametersPhoto) {
        const file = await this.handleComponentChange(parameter, id)
        if (file?.length) {
          newPhotos.push(...file)
        }
      }
      return newPhotos
    }

    async handleComponentChange (parameter: any, id: number) {
      const photosFetched = await this.fetchData({
        query: { name: 'find', model: 'FileProcess' },
        filter: {
          _and: [
            { id_process_record: { _eq: id } },
            { id_file_parameter: { _eq: parameter.id } },
          ],
        },
        force: true,
      })

      return photosFetched.length ? photosFetched : []
    }
  }
